import { createClient, type SupabaseClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL ?? ''
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY ?? ''
const authUrl = import.meta.env.VITE_BETTER_AUTH_URL ?? ''

let cachedClient: SupabaseClient | null = null

/**
 * Returns a cached Supabase client configured with third-party JWT exchange.
 *
 * The client exchanges the better-auth bearer token for a Supabase-compatible
 * JWT via the `/auth/supabase-token` endpoint. Session persistence and
 * auto-refresh are disabled to avoid conflicts with the external JWT flow.
 *
 * @param getToken Function that returns the current bearer token, or null if unauthenticated
 */
export function getSystemsSupabaseClient(
  getToken: () => string | null
): SupabaseClient | null {
  if (!supabaseUrl || !supabaseAnonKey) {
    return null
  }

  if (cachedClient) {
    return cachedClient
  }

  cachedClient = createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      persistSession: false,
      autoRefreshToken: false,
    },
    accessToken: async () => {
      const token = getToken()
      if (token == null) {
        return null
      }

      try {
        const response = await fetch(`${authUrl}/supabase-token`, {
          headers: { Authorization: `Bearer ${token}` },
        })

        if (!response.ok) {
          console.error(
            `Failed to fetch Supabase token: HTTP ${response.status}`
          )
          return null
        }

        const body = await response.json()

        // Support both flat and nested response shapes:
        //   { token: "..." } or { access_token: "..." }
        //   { data: { token: "..." } } or { data: { access_token: "..." } }
        const data = body.data ?? body
        return data.token ?? data.access_token ?? null
      } catch (error) {
        console.error('Error fetching Supabase token:', error)
        return null
      }
    },
  })

  return cachedClient
}
